// X216 (COUNTRY_PLAN.md, Grant 2026-10-28): a club's country, with or without a venue. The API asks for it on
// auth/me (`clubsNeedingCountry`, the clubs this admin runs that have none, each with a suggestion). The web asks on
// any page after sign-in, in ONE dialog for all of them, one tap when there is a suggestion. The rules live here,
// pure, so the prompt, the Needs you row and the tests agree.
import {mfaHeld} from '~/utils/mfa';
import {zoneLabel} from '~/utils/clubClock';
import {countryInSentence, withDisplayName} from '~/utils/countryNames';

/** X221 (R90 L-3, L-4): a country as a sentence says it, by its natural name ("the United Kingdom", "the Isle of Man", "Spain"). */
const inSentence = (name, iso = null) => countryInSentence({name, isoCode: iso});

export const COUNTRY_TITLE = (clubName) => `Where is ${clubName} based?`;
export const COUNTRY_TITLE_MANY = 'Where are your clubs based?';
export const COUNTRY_LINE = 'Choose the country your club is in. We use it for match times, prices and card fees.';
export const COUNTRY_LINE_MANY = 'Choose the country each club is in. We use it for match times, prices and card fees.';
export const COUNTRY_SAVED = 'Country saved';
export const COUNTRIES_SAVED = 'Countries saved';
export const COUNTRY_HELP = 'Where the club is run from.';
export const COUNTRY_NEEDS_YOU_TITLE = "Add your club's country";
export const COUNTRY_NEEDS_YOU_LINE = 'We use it for match times, prices and card fees.';

/**
 * X225 (R94): the sign-in, security and flow pages: signing in and up, the setup and prompt walls, two-step sign-in and
 * every /security/* page (verify, setup, step-up "Confirm it's you"), the e-mail and token pages, and EVERY invite page
 * (Grant 2026-10-28: /invite/*, join links, player-claim links). ONE list for everything that must never sit over such
 * a page: the country question (X216) and the assistant's "Meet the assistant" coach mark (X225).
 */
export const FLOW_PAGE_PATHS = Object.freeze([
  '/login', '/register', '/details-required', '/prompt', '/security/', '/welcome/', '/forgot-password',
  '/validate/', '/login-with-token/', '/auth/', '/invite/', '/join', '/claim', '/pay/', '/book/', '/memberships/',
  '/tv/', '/embed/',
  // X221 (R90 L-6): the meets e-mail token pages (a spot offered, a billing choice) and Stripe's onboarding return
  '/meets/offer/', '/meets/billing/', '/stripe-onboarding/',
  // X222 (R91-M1): e-mail preferences is only ever opened from an e-mail (its token link) or to manage e-mails
  '/email-preferences',
]);

/** Pages the country question never covers: the flow pages, plus the super console (super mode is not acting as a club admin). */
export const COUNTRY_PROMPT_SKIPPED_PATHS = Object.freeze([...FLOW_PAGE_PATHS, '/admin/super']);

/**
 * X221 (R90 L-6): e-mail and push links that open an action on an ordinary page (pay for a booking or membership,
 * release a court, unsubscribe with a token) land on that action alone, never with the country question over it.
 */
export const COUNTRY_PROMPT_SKIPPED_QUERY = Object.freeze(['pay', 'release', 'rsvp', 'approve', 'unsubscribe']);

export function isCountryPromptSkippedQuery(path, query) {
  const q = query ?? {};
  if (COUNTRY_PROMPT_SKIPPED_QUERY.some((k) => q[k] != null && q[k] !== '')) return true;
  const p = String(path || '').split('?')[0];
  return p === '/email-preferences' && q.t != null && q.t !== ''; // the token (unsubscribe) link from an e-mail
}

const basePath = (path) => String(path || '').split('?')[0].split('#')[0];

/**
 * X222 (R91-M1): the skip is decided ONCE per landing. A page opened from an e-mail link may tidy its own address on
 * mount (email-preferences drops ?t=, the memberships panel drops ?pay= on close), and the prompt re-checks on every
 * route change; without this it would see the tidied address and ask over the very page the link opened. Returns the
 * path that stays skipped: the current path when it was entered with a skip query, the remembered one while the
 * visitor stays on that same path, and null once they go to a different path.
 */
export function nextLandingSkipPath(previous, path, query) {
  const p = basePath(path);
  if (isCountryPromptSkippedQuery(p, query)) return p;
  return previous && previous === p ? previous : null;
}

/** Layouts with no room for a dialog: the clubhouse TV, embedded widgets and the bare token pages. */
export const COUNTRY_PROMPT_SKIPPED_LAYOUTS = Object.freeze(['tv', 'embed', 'bare']);

const matchesPathList = (list, path) => {
  const p = basePath(path);
  return list.some((s) => (s.endsWith('/') ? p.startsWith(s) : p === s || p.startsWith(`${s}/`)));
};

export const isCountryPromptSkippedPath = (path) => matchesPathList(COUNTRY_PROMPT_SKIPPED_PATHS, path);

/** X225: a flow page by its path alone (see FLOW_PAGE_PATHS). */
export const isFlowPagePath = (path) => matchesPathList(FLOW_PAGE_PATHS, path);

/**
 * X225 (R94): whether this page view is a sign-in / security / flow page: a flow path, a tv / embed / bare layout, an
 * action landing (?pay= ?release= ?rsvp= ?approve= ?unsubscribe=, /email-preferences?t=), or the path such a landing
 * was entered on while the visitor stays there (landingSkipPath, X222).
 */
export function isFlowPageView({path = '', query = null, layout = null, landingSkipPath = null} = {}) {
  if (layout && COUNTRY_PROMPT_SKIPPED_LAYOUTS.includes(layout)) return true;
  if (isFlowPagePath(path)) return true;
  if (isCountryPromptSkippedQuery(path, query)) return true;
  return !!landingSkipPath && landingSkipPath === basePath(path);
}

/**
 * Whether the country question opens now. After the launch refresh (the cookie copy of the user carries no list),
 * never over another wall or prompt (those go first: two-step, account setup, a form prompt, the user tag step,
 * another dialog already open), never on a skipped page or layout, and only with clubs to ask about.
 */
export function shouldAskCountry({user, refreshed = true, path = '', query = null, layout = null, otherPromptOpen = false, dismissed = false, landingSkipPath = null}) {
  if (!user?.id || !refreshed || dismissed || otherPromptOpen) return false;
  if (!Array.isArray(user.clubsNeedingCountry) || user.clubsNeedingCountry.length === 0) return false;
  if (mfaHeld(user)) return false;
  if (user.setupRequired) return false;
  if (user.pendingFormPrompt?.id) return false;
  if (!user.tag && !user.tagSkipped) return false;
  if (isCountryPromptSkippedPath(path)) return false; // the flow pages and the super console
  return !isFlowPageView({path, query, layout, landingSkipPath});
}

/** One row per club, pre-filled with the API's suggestion; never a first-of-list default. */
export function countryRows(clubs) {
  return (clubs ?? []).map((c) => ({
    clubId: c.clubId,
    clubName: c.clubName,
    imageFileName: c.imageFileName ?? null,
    countryId: c.suggestedCountryId ?? null,
    country: c.suggestedCountryId ? withDisplayName({id: c.suggestedCountryId, name: c.suggestedCountryName, isoCode: c.suggestedCountryCode ?? null}) : null,
    suggestedCountryId: c.suggestedCountryId ?? null,
    message: null,
  }));
}

/**
 * How the dialog leads:
 *   single    – one club with a suggestion: "Is {club} in {country}?" Yes / Choose another
 *   pick      – one club with no suggestion: the picker, empty, Save off until chosen
 *   allSame   – several clubs, all suggested the same country: "Are all N clubs in {country}?" Yes, all of them
 *   mixed     – several clubs otherwise: one row each, Save off until every row has a country
 */
export function countryPromptMode(rows) {
  const list = rows ?? [];
  if (list.length === 1) return list[0].suggestedCountryId ? 'single' : 'pick';
  if (list.length > 1 && list.every((r) => r.suggestedCountryId && r.suggestedCountryId === list[0].suggestedCountryId)) return 'allSame';
  return 'mixed';
}

/** Every row has a country, so Save can be pressed. */
export const canSaveCountries = (rows) => (rows ?? []).length > 0 && rows.every((r) => !!r.countryId);

/** Whether every row still holds its own suggestion (so the one-tap button still says "Yes, all of them"). */
export const rowsUnchanged = (rows) => (rows ?? []).every((r) => r.countryId && r.countryId === r.suggestedCountryId);

/** The body of PUT clubs/country. */
export const countryAnswerBody = (rows) => (rows ?? []).map((r) => ({clubId: r.clubId, countryId: r.countryId}));

/** "Are all 4 clubs in the United Kingdom?" */
export const allSameQuestion = (rows) => `Are all ${rows.length} clubs in ${rows[0]?.country ? countryInSentence(rows[0].country) : ''}?`;

/** Rows the API did not save as asked keep their own words (Already set to Spain, You're not an admin…). */
export function applyCountryResults(rows, results) {
  const byId = new Map((results ?? []).map((r) => [r.clubId, r]));
  return rows
      .map((row) => {
        const res = byId.get(row.clubId);
        if (!res) return row;
        if (res.saved) return null;
        if (res.alreadySet && !res.message) return null; // already that country: nothing to say
        return {...row, message: res.message ?? null, settled: !!res.alreadySet};
      })
      .filter(Boolean);
}

// ── Stripe (§6.3): confirm the country at every set-up, it can't change afterwards ──

// X218 (Grant 2026-10-28): Stripe doesn't list Jersey, Guernsey or the Isle of Man on their own; their clubs keep their
// own country and their Stripe account opens as GB (mirrors the API's ClubCountryRules.StripeCountryFor).
export const STRIPE_COVERED_BY = Object.freeze({JE: 'GB', GG: 'GB', IM: 'GB'});
export const stripeCoveredBy = (iso) => (iso ? STRIPE_COVERED_BY[String(iso).toUpperCase()] ?? null : null);
export const stripeCountryLead = (name, iso = null) => (stripeCoveredBy(iso)
  ? `Your Stripe account will be set up in the United Kingdom (Stripe covers ${inSentence(name, iso)} under the UK).`
  : `Your Stripe account will be set up in ${inSentence(name, iso)}.`);
export const STRIPE_COUNTRY_LINE = "Stripe can't change this later, so check it's where your club and its bank account are.";
export const stripeCountryYes = (name, iso = null) => (stripeCoveredBy(iso) ? 'Yes, set up in the United Kingdom' : `Yes, set up in ${inSentence(name, iso)}`);
export const STRIPE_COUNTRY_CHOOSE = 'Which country is your club and its bank account in?';
/** Club settings: changing a club's country never moves its Stripe account. */
export const stripeStaysNote = (name, iso = null) => `Your Stripe account stays in ${inSentence(name, iso)}.`;
/** Club settings: a country change never moves the booking clock (C4). */
export const bookingZoneNote = (zone) => `Booking times are in ${zoneLabel(zone)}.`;
/** A venue picked in another country moves the club's country with it, visibly. */
export const venueCountryNote = (name, iso = null) => `Your venue is in ${inSentence(name, iso)}, so we've set the club's country to ${inSentence(name, iso)}.`;

/** Google's region bias is a ccTLD: lower-case ISO, except the UK's "uk". */
export const placesRegionFor = (iso) => (!iso ? null : iso.toUpperCase() === 'GB' ? 'uk' : iso.toLowerCase());
